<template>
	<!-- Review queue alert -->
	<div
		v-if="canReview && awaitingReviewCount > 0"
		class="mb-4 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
		<app-icon :icon="['fas', 'clock']" class="text-amber-500" />
		<span>
			<strong>{{ awaitingReviewCount }}</strong>
			document{{ awaitingReviewCount === 1 ? '' : 's' }} awaiting your review.
		</span>
	</div>

	<!-- Loading skeleton -->
	<div v-if="loading" class="space-y-1.5">
		<div v-for="n in 4" :key="n" class="h-12 animate-pulse rounded-lg bg-gray-100" />
	</div>

	<template v-else>
		<p v-if="processedDocuments.length === 0" class="py-8 text-center text-sm text-gray-400">
			No documents available.
		</p>

		<template v-else>
			<div
				v-for="(section, sectionIndex) in documentSections"
				:key="section.id"
				:class="sectionIndex > 0 ? 'mt-4' : ''">
				<!-- Section heading -->
				<div v-if="section.title" class="mb-2 flex items-center gap-2 px-1">
					<app-icon
						:icon="['fas', 'folder']"
						class="text-sm"
						:class="section.iconClass" />
					<span
						class="text-xs font-semibold uppercase tracking-wide"
						:class="section.titleClass">
						{{ section.title }}
					</span>
				</div>

				<!-- Document cards -->
				<div class="space-y-1.5">
					<div
						v-for="doc in section.docs"
						:key="doc.id"
						class="overflow-hidden rounded-lg border bg-white"
						:class="cardBorderClass(doc)">
						<div class="flex">
							<!-- Left status accent bar -->
							<div class="w-1 flex-shrink-0" :class="cardAccentClass(doc)" />

							<div class="min-w-0 flex-1">
								<!-- Main card row -->
								<div class="flex items-start gap-2 p-2">
									<!-- File icon -->
									<div class="mt-0.5 flex-shrink-0">
										<file-icon
											:ext="
												isPdf(doc.displayRevision?.file?.name)
													? 'pdf'
													: 'doc'
											" />
									</div>

									<!-- Document info -->
									<div class="min-w-0 flex-1">
										<div class="flex flex-wrap items-center gap-1.5">
											<span class="text-[11px] text-gray-400">
												{{ doc.reference }}
											</span>
											<span
												v-if="doc.type?.name"
												class="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600">
												{{ doc.type.name }}
											</span>
											<!-- Status badge -->
											<template v-if="canReview && isAwaitingReview(doc)">
												<span
													class="inline-flex items-center gap-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
													<app-icon
														:icon="['fas', 'clock']"
														class="text-[9px]" />
													Awaiting Review
												</span>
											</template>
											<template v-else-if="hasPendingRevision(doc)">
												<span
													class="inline-flex items-center gap-1 rounded-full bg-green-100 px-1.5 py-0.5 text-[10px] font-medium text-green-700">
													<app-icon
														:icon="['fas', 'check-circle']"
														class="text-[9px]" />
													Approved
												</span>
												<app-tooltip
													text="A newer revision is awaiting review">
													<span
														class="inline-flex cursor-default items-center gap-1 text-[10px] font-medium text-amber-600">
														<app-icon
															:icon="['fas', 'clock']"
															class="text-[9px]" />
														Newer pending
													</span>
												</app-tooltip>
											</template>
											<template
												v-else-if="
													requiresReview(doc) && reviewStatus(doc)
												">
												<app-tooltip
													:text="
														doc.displayRevision?.review_comments ||
														'No comments'
													">
													<span
														class="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium"
														:class="
															reviewStatus(doc) === 'approved'
																? 'bg-green-100 text-green-700'
																: 'bg-red-100 text-red-700'
														">
														<app-icon
															:icon="[
																'fas',
																reviewStatus(doc) === 'approved'
																	? 'check-circle'
																	: 'circle-xmark'
															]"
															class="text-[9px]" />
														{{
															reviewStatus(doc) === 'approved'
																? 'Approved'
																: 'Rejected'
														}}
													</span>
												</app-tooltip>
											</template>
											<template
												v-else-if="!canReview && isAwaitingReview(doc)">
												<span
													class="inline-flex items-center gap-1 text-[10px] font-medium text-amber-600">
													<app-icon
														:icon="['fas', 'clock']"
														class="text-[9px]" />
													Needs review
												</span>
											</template>
										</div>
										<p class="text-sm font-semibold text-gray-800">
											{{ doc.name }}
										</p>
										<p v-if="doc.displayRevision" class="text-xs text-gray-500">
											<span class="font-medium">
												Rev. {{ doc.displayRevision.revision_no }}
											</span>
											<template v-if="doc.displayRevision.description">
												&mdash; {{ doc.displayRevision.description }}
											</template>
										</p>
										<!-- Metadata tags -->
										<div class="mt-1 flex flex-wrap gap-1">
											<span
												v-if="doc.language?.name"
												class="rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700">
												{{ doc.language.name }}
											</span>
											<span
												v-if="doc.brand"
												class="rounded bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-700">
												{{ doc.brand }}
											</span>
											<span
												v-if="showBusinessUnitColumn && doc.business_unit"
												class="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600">
												{{ doc.business_unit }}
											</span>
										</div>
										<div
											v-if="doc.products?.length || doc.product_categories?.length"
											class="mt-1.5 flex flex-wrap items-center gap-1 text-[10px]">
											<span class="font-medium text-gray-400">Available on:</span>
											<router-link
												v-for="product in doc.products || []"
												:key="`product-${product.id}`"
												:to="`/product-data/products/${product.id}`"
												class="rounded bg-blue-50 px-1.5 py-0.5 font-medium text-blue-700 hover:bg-blue-100">
												{{ product.name }}
											</router-link>
											<router-link
												v-for="category in doc.product_categories || []"
												:key="`category-${category.id}`"
												:to="`/product-data/product-categories/${category.id}`"
												class="rounded bg-green-50 px-1.5 py-0.5 font-medium text-green-700 hover:bg-green-100">
												{{ category.name }}
											</router-link>
										</div>
									</div>

									<!-- Action buttons -->
									<div class="flex flex-shrink-0 items-start">
										<div class="flex items-center gap-1">
											<template v-if="canReview && isAwaitingReview(doc)">
												<app-tooltip text="Approve or reject this document">
													<app-button
														icon="clipboard-check"
														theme="warning"
														size="small"
														@click.stop="
															openReviewForm(doc, doc.latestRevision)
														">
														Review
													</app-button>
												</app-tooltip>
											</template>

											<app-tooltip
												v-if="canEdit && !productId"
												text="Manage Links">
												<app-button
													icon="fa-pencil"
													theme="outline"
													size="small"
													@click.stop="openLinksForm(doc)" />
											</app-tooltip>

											<!-- No file available -->
											<span
												v-if="!doc.displayRevision?.file?.download_url"
												class="inline-flex items-center gap-1 text-[11px] italic text-gray-400">
												<app-icon
													:icon="['fas', 'file-slash']"
													class="text-[10px]" />
												No file
											</span>

											<app-tooltip
												v-if="
													doc.displayRevision?.file?.download_url &&
													isPdf(doc.displayRevision.file.name)
												"
												text="View Latest Revision">
												<app-button
													icon="eye"
													theme="secondary"
													size="small"
													@click.stop="
														viewFile(
															doc.displayRevision.file.download_url
														)
													" />
											</app-tooltip>

											<app-tooltip
												v-if="doc.displayRevision?.file?.download_url"
												text="Download Latest Revision">
												<app-button
													:to="doc.displayRevision.file.download_url"
													icon="download"
													size="small"
													theme="primary" />
											</app-tooltip>

											<app-tooltip
												v-if="
													canEdit &&
													!readonly &&
													doc.displayRevision?.file?.download_url &&
													(!productId || doc.level === 'Product')
												"
												text="Remove">
												<app-button
													icon="trash"
													theme="danger"
													size="small"
													@click.stop="removeDocument(doc)" />
											</app-tooltip>

											<app-tooltip
												v-if="
													(canEdit || canReview) &&
													(!productId || doc.level === 'Product') &&
													doc.revisions?.length > 0
												"
												:text="
													isExpanded(doc)
														? 'Hide Revisions'
														: 'Show Revisions'
												">
												<button
													class="rounded border border-gray-200 bg-white px-2 py-1 text-xs text-gray-500 transition hover:bg-gray-50 hover:text-gray-700"
													@click.stop="toggleRevisions(doc)">
													<app-icon
														:icon="[
															'fas',
															isExpanded(doc)
																? 'angle-up'
																: 'angle-down'
														]" />
												</button>
											</app-tooltip>
										</div>
									</div>
								</div>

								<!-- Product page links -->
								<div
									v-if="
										(productId || productName) && doc.productLinks?.length > 0
									"
									class="flex flex-wrap items-center gap-2 border-t border-gray-100 bg-gray-50 px-3 py-1.5">
									<span class="text-[11px] font-medium text-gray-400">
										Jump to:
									</span>
									<app-tooltip
										v-for="link in doc.productLinks"
										:key="link.id"
										:text="`View: ${link.description}`">
										<app-button
											v-if="
												doc.displayRevision?.file?.download_url &&
												isPdf(doc.displayRevision.file.name)
											"
											theme="success"
											size="small"
											@click.stop="
												viewFile(
													doc.displayRevision.file.download_url,
													link.page_number
												)
											">
											{{ link.description }}
										</app-button>
									</app-tooltip>
								</div>

								<!-- Revision history (expanded, editors/reviewers) -->
								<div
									v-if="isExpanded(doc)"
									class="border-t border-gray-100 bg-gray-50 px-3 py-2">
									<div class="ml-7">
										<!-- Add revision -->
										<button
											v-if="canEdit"
											class="mb-2 flex items-center gap-1.5 text-[11px] font-medium text-blue-600 transition hover:text-blue-700"
											@click.stop="openAddRevisionForm(doc)">
											<app-icon :icon="['fas', 'plus']" class="text-[10px]" />
											Add new revision
										</button>

										<!-- Revision list with timeline -->
										<div class="space-y-1 border-l-2 border-gray-200 pl-3">
											<div
												v-for="rev in sortRevisions(doc.revisions || [])"
												:key="rev.id"
												class="flex items-center gap-2 rounded border bg-white px-2 py-1.5"
												:class="
													canReview &&
													requiresReview(doc) &&
													!rev.review_status
														? 'border-amber-200'
														: 'border-gray-100'
												">
												<!-- Rev number -->
												<span
													class="w-10 flex-shrink-0 text-[11px] font-semibold text-gray-700">
													Rev. {{ rev.revision_no }}
												</span>

												<!-- Description -->
												<span
													v-if="rev.description"
													class="min-w-0 flex-1 truncate text-[11px] text-gray-500">
													{{ rev.description }}
												</span>
												<div v-else class="flex-1" />

												<!-- Status + actions -->
												<div
													class="flex flex-shrink-0 items-center gap-1.5">
													<template
														v-if="
															requiresReview(doc) && rev.review_status
														">
														<app-tooltip
															:text="
																rev.review_comments || 'No comments'
															">
															<span
																class="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-medium"
																:class="
																	rev.review_status === 'approved'
																		? 'bg-green-100 text-green-700'
																		: 'bg-red-100 text-red-700'
																">
																<app-icon
																	:icon="[
																		'fas',
																		rev.review_status ===
																		'approved'
																			? 'check-circle'
																			: 'circle-xmark'
																	]"
																	class="text-[9px]" />
																{{
																	rev.review_status === 'approved'
																		? 'Approved'
																		: 'Rejected'
																}}
															</span>
														</app-tooltip>
													</template>

													<template
														v-if="
															canReview &&
															requiresReview(doc) &&
															!rev.review_status
														">
														<app-button
															size="small"
															theme="warning"
															icon="clipboard-check"
															@click.stop="openReviewForm(doc, rev)">
															Review
														</app-button>
													</template>

													<app-menu v-if="rev.file?.download_url">
														<app-menu-item
															v-if="isPdf(rev.file.name)"
															icon="eye"
															@click="
																viewFile(rev.file.download_url)
															">
															View PDF
														</app-menu-item>
														<app-menu-item
															icon="download"
															:to="rev.file.download_url">
															Download
														</app-menu-item>
													</app-menu>

													<span
														v-else
														class="text-[10px] italic text-gray-300">
														No file
													</span>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</template>
	</template>

	<add-revision-form
		:show="showAddRevisionForm"
		:document="selectedDocument"
		@close="showAddRevisionForm = false"
		@revision-added="onRevisionAdded" />

	<document-links-form
		:show="showLinksForm"
		:document="selectedDocument"
		:products="products"
		@close="showLinksForm = false" />

	<review-document-form
		:show="showReviewForm"
		:document="selectedDocument"
		:revision="selectedRevision"
		@close="showReviewForm = false"
		@reviewed="onReviewed" />
</template>

<script lang="ts" setup>
	import { computed, ref, watch } from 'vue'
	import type { Document } from '@/data/schema/document'
	import type { DocumentRevision } from '@/data/schema/document-revision'
	import FileIcon from '@/components/partials/icons/FileIcon.vue'
	import useAuthStore from '@/stores/auth'
	import AppButton from '@/components/base/AppButton.vue'
	import DocumentService from '@/services/document'
	import AddRevisionForm from '@/components/forms/AddRevisionForm.vue'
	import DocumentLinksForm from '@/components/forms/DocumentLinksForm.vue'
	import AppTooltip from '@/components/base/AppTooltip.vue'
	import AppMenu from '@/components/base/AppMenu.vue'
	import AppMenuItem from '@/components/base/AppMenuItem.vue'
	import ReviewDocumentForm from '@/components/forms/ReviewDocumentForm.vue'

	const emit = defineEmits(['document-removed', 'refresh-documents', 'update:expandedRows'])

	const props = withDefaults(
		defineProps<{
			documents?: Document[]
			products?: any[]
			loading?: boolean
			readonly?: boolean
			productId?: string | number
			productName?: string
			showLevelColumn?: boolean
			showBusinessUnitColumn?: boolean
			expandedRows?: Record<string, boolean>
		}>(),
		{
			documents: () => [],
			products: () => [],
			loading: false,
			readonly: false,
			productId: undefined,
			productName: undefined,
			showLevelColumn: false,
			showBusinessUnitColumn: true,
			expandedRows: undefined
		}
	)

	const authStore = useAuthStore()
	const canEdit = computed(() => !props.readonly && authStore.isAbleTo(['edit-product-range']))
	const canReview = computed(() => authStore.isAbleTo(['review_documents']))

	const requiresReview = (data: any) => {
		const doc = data?._rowType === 'revision' ? data._parentDoc : data
		return !!doc?.type?.requires_review
	}

	const reviewStatus = (data: any) => {
		if (data?._rowType === 'revision') return data.review_status ?? null
		return data?.displayRevision?.review_status ?? null
	}

	const isAwaitingReview = (data: any) => {
		return (
			requiresReview(data) && !!data?.latestRevision && !data?.latestRevision?.review_status
		)
	}

	const hasPendingRevision = (data: any) => {
		return (
			!canReview.value &&
			data?.type?.requires_review &&
			!!data?.latestRevision &&
			data?.latestRevision?.review_status === null &&
			!!data?.displayRevision &&
			data?.displayRevision?.id !== data?.latestRevision?.id
		)
	}

	const _expandedRows = ref<Record<string, boolean>>({})

	const expandedRows = computed({
		get() {
			return props.expandedRows !== undefined ? props.expandedRows : _expandedRows.value
		},
		set(value: Record<string, boolean>) {
			_expandedRows.value = value
			emit('update:expandedRows', value)
		}
	})

	watch(
		() => props.expandedRows,
		(newVal) => {
			if (
				newVal !== undefined &&
				JSON.stringify(newVal) !== JSON.stringify(_expandedRows.value)
			) {
				_expandedRows.value = newVal
			}
		},
		{ deep: true }
	)

	const isExpanded = (row: Document) => !!expandedRows.value[row.id]

	const sortRevisions = (revisions: DocumentRevision[]) =>
		[...revisions].sort((a, b) => parseInt(b.revision_no) - parseInt(a.revision_no))

	const getLatestRevision = (doc: Document): DocumentRevision | null => {
		if (!doc.revisions?.length) return null
		return sortRevisions(doc.revisions)[0]
	}

	const getLatestApprovedRevision = (doc: Document): DocumentRevision | null => {
		if (!doc.revisions?.length) return null
		return sortRevisions(doc.revisions).find((r) => r.review_status === 'approved') ?? null
	}

	const getProductLinks = (doc: Document) => {
		if ((!props.productId && !props.productName) || !doc.links) return []

		return doc.links.filter((link) => {
			const idMatch = props.productId && link.product_id == props.productId

			let nameMatch = false
			if (props.productName) {
				const pName = props.productName.toLowerCase()
				if (link.product_name && link.product_name.toLowerCase() === pName) {
					nameMatch = true
				} else if (link.description && link.description.toLowerCase() === pName) {
					nameMatch = true
				}
			}

			return idMatch || nameMatch
		})
	}

	const processedDocuments = computed(() => {
		return props.documents.map((doc) => {
			const latestRevision = getLatestRevision(doc)
			const latestApprovedRevision = doc.type?.requires_review
				? getLatestApprovedRevision(doc)
				: null

			const displayRevision =
				canReview.value || !doc.type?.requires_review
					? latestRevision
					: (latestApprovedRevision ?? latestRevision)

			return {
				...doc,
				latestRevision,
				displayRevision,
				productLinks: getProductLinks(doc)
			}
		})
	})

	const visibleDocuments = computed(() =>
		processedDocuments.value.filter((d: any) => !!d.displayRevision?.file?.download_url)
	)

	const categoryDocuments = computed(() =>
		visibleDocuments.value.filter((d: any) => d.level === 'Category')
	)

	const productDocuments = computed(() =>
		visibleDocuments.value.filter((d: any) => d.level === 'Product')
	)

	const documentSections = computed(() => {
		if (!props.showLevelColumn) {
			return [
				{
					id: 'all',
					title: null as string | null,
					iconClass: '',
					titleClass: '',
					docs: visibleDocuments.value
				}
			]
		}
		const sections: {
			id: string
			title: string | null
			iconClass: string
			titleClass: string
			docs: any[]
		}[] = []
		if (categoryDocuments.value.length > 0) {
			sections.push({
				id: 'category',
				title: 'Category Documents',
				iconClass: 'text-green-600',
				titleClass: 'text-green-700',
				docs: categoryDocuments.value
			})
		}
		if (productDocuments.value.length > 0) {
			sections.push({
				id: 'product',
				title: 'Product Documents',
				iconClass: 'text-blue-600',
				titleClass: 'text-blue-700',
				docs: productDocuments.value
			})
		}
		return sections
	})

	const cardBorderClass = (doc: any) => {
		if (isAwaitingReview(doc)) return 'border-amber-300 bg-amber-50'
		const status = reviewStatus(doc)
		if (status === 'approved') return 'border-green-200'
		if (status === 'rejected') return 'border-red-200'
		return 'border-gray-200'
	}

	const cardAccentClass = (doc: any) => {
		if (isAwaitingReview(doc)) return 'bg-amber-400'
		const status = reviewStatus(doc)
		if (status === 'approved') return 'bg-green-400'
		if (status === 'rejected') return 'bg-red-400'
		return 'bg-gray-300'
	}

	const awaitingReviewCount = computed(
		() => processedDocuments.value.filter((doc) => isAwaitingReview(doc)).length
	)

	const showAddRevisionForm = ref(false)
	const showLinksForm = ref(false)
	const showReviewForm = ref(false)
	const selectedDocument = ref<Document | null>(null)
	const selectedRevision = ref<DocumentRevision | null>(null)

	const openReviewForm = (doc: any, revision: any) => {
		selectedDocument.value = doc
		selectedRevision.value = revision
		showReviewForm.value = true
	}

	const onReviewed = () => {
		showReviewForm.value = false
		emit('refresh-documents')
	}

	const openAddRevisionForm = (doc: Document) => {
		selectedDocument.value = doc
		showAddRevisionForm.value = true
	}

	const onRevisionAdded = () => {
		showAddRevisionForm.value = false
		emit('refresh-documents')
	}

	const openLinksForm = (doc: Document) => {
		selectedDocument.value = doc
		showLinksForm.value = true
	}

	const toggleRevisions = (row: Document) => {
		const newExpandedState = { ...expandedRows.value }
		if (newExpandedState[row.id]) {
			delete newExpandedState[row.id]
		} else {
			newExpandedState[row.id] = true
		}
		expandedRows.value = newExpandedState
	}

	const isPdf = (filename?: string) => !!filename && filename.toLowerCase().endsWith('.pdf')

	const pdfUrl = (url: string, page?: number) => (page ? `${url}#page=${page}` : url)

	const viewFile = async (url: string, page?: number) => {
		const viewerWindow = window.open('', '_blank')

		try {
			const response = await fetch(url)
			if (!response.ok) throw new Error('Network response was not ok.')
			const originalBlob = await response.blob()
			const newBlob = originalBlob.slice(0, originalBlob.size, 'application/pdf')
			const objectUrl = URL.createObjectURL(newBlob)
			const viewUrl = pdfUrl(objectUrl, page)

			if (viewerWindow) {
				viewerWindow.location.href = viewUrl
			} else {
				window.open(viewUrl, '_blank')
			}

			setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
		} catch (e) {
			console.error('Could not view PDF, falling back to direct link:', e)

			if (viewerWindow) {
				viewerWindow.location.href = pdfUrl(url, page)
			} else {
				window.open(pdfUrl(url, page), '_blank')
			}
		}
	}

	const removeDocument = async (doc: Document) => {
		if (confirm(`Are you sure you want to remove the document "${doc.name}"?`)) {
			const documentService = new DocumentService()
			try {
				await documentService.delete(doc.id)
				emit('document-removed')
			} catch (error) {
				console.error('Error removing document:', error)
			}
		}
	}
</script>
